/**
 * ADR-0045 §6 — the unified user catalogue: ONE row per person, whoever
 * made them and wherever they have access.
 *
 * Why this exists (Ed, 2026-09-17): the Users & Roles list showed only
 * production `_practitionerRole` grants, so anyone who holds access ONLY in a
 * non-production environment — an invited consultant, a staff member added to
 * demo — was invisible there, and "the full list of users I have created"
 * lived in two places. Accounts stay separate per environment (the whole point
 * of ADR-0045); only this READ merges them.
 *
 * Two sources, both already in production:
 *   - `_practitionerRole` rows (production access; OneTouch-synced and manual)
 *   - the guest list `_ops/environment_access/entries` (every non-production
 *     account the invite path minted, each tagged with its `environment`)
 *
 * They join on EMAIL, the only thing a production person and a tenant account
 * share (uids differ by design). A guest entry whose email matches no
 * production person becomes its own row: someone who exists only outside
 * production.
 *
 * OMON-348 (2026-09-21) adds a THIRD source: family contacts — every
 * `_relatedPerson` link (person × client) with its Circle of Support state.
 * Ed: *"my model is that the 'users' screen is literally ALL users of any
 * kind"* and *"one row per email address."* Until this they were absent
 * (~838 of them). They join on the same EMAIL key, so a carer who is also a
 * relative lands in ONE row — a display join only; nothing is merged in the
 * data. ☠️ One row per email also surfaces a shared inbox: 58 addresses carry
 * contacts with DIFFERENT names (couples, households). Under email sign-in
 * whoever holds that inbox can prove any of them, so the row says so rather
 * than pretending they are separable.
 *
 * Pure — no Firestore imports — so vitest can exercise the join.
 */
import type { AccessState } from "../clients/family-contacts";
import type { PractitionerRoleRow } from "./loaders";

/** One family-contact link (person × client), as `list-family` returns it. */
export interface FamilyLink {
  relatedPersonDocId: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  relationships: string[];
  clientName: string | null;
  accessState: AccessState;
  /** Where the CONTACT came from — read server-side from the contact's own
   *  `meta.oneTouchContactId`, never the person record's origin note, which
   *  says "stc" for 833 of 838 OneTouch contacts. */
  source?: "onetouch" | "stc";
  /** The client's episode of care — the person card's "Family of X →" link
   *  target (`#/clients/<eocId>/…`). Null when the contact has none. */
  eocId?: string | null;
  /** The contact's photo (ADR-0031 256px thumbnail, bucket-relative), the
   *  same one the client card's Family & contacts shows. Null → initials. */
  photoPath?: string | null;
}

export const PRODUCTION = "production";

/** What the guest-list callable returns for one non-production account. */
export interface EnvironmentEntry {
  uid: string;
  email: string;
  given?: string;
  kind?: "guest" | "staff";
  expires: string | null;
  role: string;
  /** Tenant-backed environment the account lives in. Entries written before
   *  2026-09-17 (#1939) carry none — they were all training. */
  environment?: string;
  disabledAt: string | null;
}

export type AccessStatus = "active" | "revoked" | "expired";

export interface EnvironmentAccess {
  environment: string;
  role: string;
  status: AccessStatus;
  expires: string | null;
  kind: "guest" | "staff" | null;
  uid: string;
}

export interface CatalogueRow {
  /** Stable key: the production person doc id, or `env:<email>`. */
  key: string;
  /** Production person, when there is one. */
  personDocId: string | null;
  name: string | null;
  email: string | null;
  /** Production role grants (empty for a non-production-only person). */
  productionRoles: PractitionerRoleRow[];
  /** Non-production accounts, one per environment entry. */
  environments: EnvironmentAccess[];
  /** OMON-348: family-contact links — Circle of Support access, per client. */
  family: FamilyLink[];
  /** Origin of the production person record (`_provenance.origin`:
   *  "onetouch" | "stc" | "guest"), when there is one. Reliable for STAFF —
   *  measured 2026-09-21: all 475 "onetouch" carry a OneTouch identifier and
   *  none of the 28 "stc" do. */
  staffOrigin: string | null;
  /** ADR-0046 §6 (amended 2026-09-22): this row is a CLIENT — listed here,
   *  never created or clinically edited here (their care lives on the client
   *  page). `circle` = every family link whose episode is theirs. */
  client?: ClientFacts;
}

/** What the list knows about a client: the planner's own client list
 *  (state.patients, keyed by episode of care) joined to family links. */
export interface ClientInfo {
  eocId: string;
  name: string;
  status: string;
  hasPlan: boolean;
  /** The live plan's version and whether a draft is open — already on the
   *  planner's client list, so the card's plan line needs no extra read. */
  planVersion?: number;
  hasDraft?: boolean;
}

export interface ClientFacts extends Omit<ClientInfo, "name"> {
  circle: FamilyLink[];
}

/**
 * WHY this row is in the list — as a list, because a row is a join of
 * sources and one person can be here for several reasons at once (a carer who
 * is also someone's daughter is "OneTouch staff" AND "Family of Mum"). A single
 * category would force a false choice, so this is DERIVED from the sources the
 * row already has; nothing is stored. Ed, 2026-09-21: "something … that
 * indicates why they are in the list".
 */
export function whyListed(row: CatalogueRow): string[] {
  const why: string[] = [];
  if (row.client) why.push("Client");
  if (row.productionRoles.length > 0) {
    why.push(
      row.staffOrigin === "onetouch" ? "OneTouch staff"
        : row.staffOrigin === "stc" ? "Added in SweetTree"
          : row.staffOrigin === "guest" ? "Added as a guest"
            : "Staff",
    );
  }
  const invited = Array.from(new Set(row.environments.map((e) => e.environment))).sort();
  if (invited.length > 0) why.push(`Invited to ${invited.join(" · ")}`);

  const clients = (source: "onetouch" | "stc") => Array.from(new Set(
    row.family
      .filter((l) => (l.source ?? "onetouch") === source)
      .map((l) => l.clientName ?? "a client"),
  ));
  const fromOneTouch = clients("onetouch");
  const addedHere = clients("stc");
  if (fromOneTouch.length > 0) why.push(`Family of ${fromOneTouch.join(" · ")}`);
  if (addedHere.length > 0) why.push(`Family of ${addedHere.join(" · ")} (added in SweetTree)`);
  return why;
}

/** Every distinct name on file for this row's family links. */
export function familyNames(row: CatalogueRow): string[] {
  const out: string[] = [];
  for (const l of row.family) if (l.name && !out.includes(l.name)) out.push(l.name);
  return out;
}

/** ☠️ More than one person behind one address: whoever holds the inbox can
 *  prove any of them. The office must see this before granting. */
export function sharesAddress(row: CatalogueRow): boolean {
  return row.email !== null && familyNames(row).length > 1;
}

/** Has Circle of Support access for at least one client right now. */
export function hasFamilyAccess(row: CatalogueRow): boolean {
  return row.family.some((l) => l.accessState === "using" || l.accessState === "granted");
}

export function normaliseEmail(email: string | null | undefined): string | null {
  const e = (email ?? "").trim().toLowerCase();
  return e.includes("@") ? e : null;
}

export function entryEnvironment(e: EnvironmentEntry): string {
  return e.environment || "training";
}

export function accessStatus(e: EnvironmentEntry, today: string): AccessStatus {
  if (e.disabledAt) return "revoked";
  // Open-ended (staff) grants never expire; only a dated guest grant lapses.
  if (e.expires && e.expires <= today) return "expired";
  return "active";
}

/**
 * Join production role rows and environment entries into person rows.
 *
 * `personEmails` maps production person doc id → email (null when the person
 * record carries none — they can still appear, they just can't be joined).
 */
export function buildCatalogue(
  roleRows: PractitionerRoleRow[],
  personEmails: Map<string, string | null>,
  entries: EnvironmentEntry[],
  today: string,
  family: FamilyLink[] = [],
  /** production person doc id → `_provenance.origin` (for `whyListed`). */
  personOrigins: Map<string, string | null> = new Map(),
  /** ADR-0046 §6: every client, a row each (no email to join on). */
  clients: ClientInfo[] = [],
): CatalogueRow[] {
  const byKey = new Map<string, CatalogueRow>();
  const keyByEmail = new Map<string, string>();

  for (const r of roleRows) {
    const key = r.personDocId ?? `role:${r.roleId}`;
    let row = byKey.get(key);
    if (!row) {
      const email = r.personDocId ? normaliseEmail(personEmails.get(r.personDocId)) : null;
      row = {
        key, personDocId: r.personDocId, name: r.personName, email,
        productionRoles: [], environments: [], family: [],
        staffOrigin: r.personDocId ? (personOrigins.get(r.personDocId) ?? null) : null,
      };
      byKey.set(key, row);
      // First production person to claim an email owns it; a duplicate is a
      // data problem the person card surfaces, not something to merge away.
      if (email && !keyByEmail.has(email)) keyByEmail.set(email, key);
    }
    row.productionRoles.push(r);
  }

  for (const e of entries) {
    const email = normaliseEmail(e.email);
    const access: EnvironmentAccess = {
      environment: entryEnvironment(e),
      role: e.role,
      status: accessStatus(e, today),
      expires: e.expires ?? null,
      kind: e.kind ?? null,
      uid: e.uid,
    };
    const existing = email ? keyByEmail.get(email) : undefined;
    if (existing) {
      byKey.get(existing)!.environments.push(access);
      continue;
    }
    const key = email ? `env:${email}` : `env-uid:${e.uid}`;
    let row = byKey.get(key);
    if (!row) {
      row = {
        key, personDocId: null, name: e.given?.trim() || null, email,
        productionRoles: [], environments: [], family: [], staffOrigin: null,
      };
      byKey.set(key, row);
      if (email) keyByEmail.set(email, key);
    }
    row.environments.push(access);
  }

  // OMON-348: family links join on the same email. No email → a row per
  // contact, because there is nothing honest to group on.
  for (const link of family) {
    const email = normaliseEmail(link.email);
    const existing = email ? keyByEmail.get(email) : undefined;
    if (existing) {
      const row = byKey.get(existing)!;
      row.family.push(link);
      if (!row.name && link.name) row.name = link.name;
      continue;
    }
    const key = email ? `fam:${email}` : `fam-doc:${link.relatedPersonDocId}`;
    const row: CatalogueRow = {
      key, personDocId: null, name: link.name, email,
      productionRoles: [], environments: [], family: [link], staffOrigin: null,
    };
    byKey.set(key, row);
    if (email) keyByEmail.set(email, key);
  }

  // ADR-0046 §6 (amended 2026-09-22): "a list of persons that doesn't show
  // all persons seems strange" (Ed). Each client is a row; their circle is
  // every family link on their episode — who can see them in Circle of
  // Support, an access view nothing else gives.
  for (const c of clients) {
    byKey.set(`client:${c.eocId}`, {
      key: `client:${c.eocId}`, personDocId: null, name: c.name || null, email: null,
      productionRoles: [], environments: [], family: [], staffOrigin: null,
      client: {
        eocId: c.eocId, status: c.status, hasPlan: c.hasPlan,
        planVersion: c.planVersion, hasDraft: c.hasDraft,
        circle: family.filter((l) => l.eocId === c.eocId),
      },
    });
  }

  const rows = Array.from(byKey.values());
  for (const row of rows) {
    row.client?.circle.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    row.environments.sort((a, b) => a.environment.localeCompare(b.environment));
    row.family.sort((a, b) => (a.clientName ?? "").localeCompare(b.clientName ?? ""));
  }
  rows.sort((a, b) => sortName(a).localeCompare(sortName(b)));
  return rows;
}

function sortName(row: CatalogueRow): string {
  // Unnamed rows sort after named ones, by email then key.
  return row.name ?? `￿${row.email ?? row.key}`;
}

/** Environments a row has ACCESS in (production when any grant is in effect;
 *  a non-production environment when its account is not revoked or expired). */
export function environmentsWithAccess(row: CatalogueRow): string[] {
  const out: string[] = [];
  if (row.productionRoles.some((r) => !r.inactive)) out.push(PRODUCTION);
  for (const e of row.environments) {
    if (e.status === "active" && !out.includes(e.environment)) out.push(e.environment);
  }
  return out;
}

/** "all" | "production" | an environment name | "non-production-only"
 *  | "family" (OMON-348: anyone who is a family contact of a client). */
export type EnvironmentFilter = string;

export const FAMILY_FILTER = "family";
export const CLIENT_FILTER = "clients";

/** A family contact is a PRODUCTION person (the contacts come from the live
 *  system), and so is a client — "outside production only" includes neither. */
function outsideProductionOnly(row: CatalogueRow): boolean {
  return row.productionRoles.length === 0 && row.family.length === 0 && !row.client;
}

/** A client whose circle includes at least one person with access now. */
function circleWithAccess(row: CatalogueRow): number {
  return (row.client?.circle ?? [])
    .filter((l) => l.accessState === "using" || l.accessState === "granted").length;
}

export function matchesEnvironment(row: CatalogueRow, filter: EnvironmentFilter): boolean {
  if (filter === "all") return true;
  if (filter === "non-production-only") return outsideProductionOnly(row);
  if (filter === PRODUCTION) return row.productionRoles.length > 0;
  if (filter === FAMILY_FILTER) return row.family.length > 0;
  if (filter === CLIENT_FILTER) return !!row.client;
  return row.environments.some((e) => e.environment === filter);
}

export function matchesCatalogueSearch(row: CatalogueRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    row.name, row.email, row.personDocId,
    ...row.productionRoles.flatMap((r) => [r.role, r.dataScope, r.roleId]),
    ...row.environments.flatMap((e) => [e.environment, e.role, e.status]),
    // OMON-348: find family by their own name, a shared name on the same
    // address, their phone, or the CLIENT they are family of.
    ...row.family.flatMap((l) => [l.name, l.phone, l.clientName, ...l.relationships]),
    // A client is found by their own name, their status, or the name of
    // anyone in their circle.
    ...(row.client ? [row.client.status, ...row.client.circle.map((l) => l.name)] : []),
    // …and WHY they are here, so "onetouch staff", "invited" or "family of"
    // narrow the list too.
    ...whyListed(row),
  ];
  return haystack.some((v) => typeof v === "string" && v.toLowerCase().includes(q));
}

export interface CatalogueSummary {
  people: number;
  productionPeople: number;
  nonProductionOnly: number;
  /** environment → people with an ACTIVE account there */
  activeByEnvironment: Record<string, number>;
  /** OMON-348: rows that are family contacts of at least one client. */
  familyPeople: number;
  /** …and of those, how many have Circle of Support access right now. */
  familyWithAccess: number;
  /** ADR-0046 §6: client rows, and how many have anyone in Circle of Support. */
  clients: number;
  clientsWithCircle: number;
}

export function summarise(rows: CatalogueRow[]): CatalogueSummary {
  const activeByEnvironment: Record<string, number> = {};
  for (const row of rows) {
    for (const env of environmentsWithAccess(row)) {
      activeByEnvironment[env] = (activeByEnvironment[env] ?? 0) + 1;
    }
  }
  return {
    people: rows.length,
    productionPeople: rows.filter((r) => r.productionRoles.length > 0).length,
    nonProductionOnly: rows.filter(outsideProductionOnly).length,
    activeByEnvironment,
    familyPeople: rows.filter((r) => r.family.length > 0).length,
    familyWithAccess: rows.filter(hasFamilyAccess).length,
    clients: rows.filter((r) => r.client).length,
    clientsWithCircle: rows.filter((r) => circleWithAccess(r) > 0).length,
  };
}

/** A `_person.telecom` email, tolerating both stored shapes (array, or the
 *  dict-of-entries the Firestore writers sometimes produce). Falls back to the
 *  login email the access sync stamps on `identifier.googleIdentityPlatform.use`. */
export function personEmail(person: Record<string, unknown> | undefined): string | null {
  if (!person) return null;
  const telecom = person.telecom;
  const entries = Array.isArray(telecom)
    ? telecom
    : telecom && typeof telecom === "object"
      ? Object.values(telecom as Record<string, unknown>)
      : [];
  for (const t of entries) {
    if (!t || typeof t !== "object") continue;
    const tt = t as { system?: unknown; value?: unknown };
    if (String(tt.system ?? "").toLowerCase() === "email") {
      const email = normaliseEmail(typeof tt.value === "string" ? tt.value : null);
      if (email) return email;
    }
  }
  const ident = person.identifier as Record<string, unknown> | undefined;
  const gip = ident?.googleIdentityPlatform as { use?: unknown } | undefined;
  return normaliseEmail(typeof gip?.use === "string" ? gip.use : null);
}
